import { GameConfig } from "@/config/GameConfig";
import type { BiteFeedbackEvent } from "@/core/types";

/**
 * QA-follow-up fix: the single source of truth for every bite/impact
 * primitive's primary color. Locked semantic table (never a per-call-site
 * literal choosing red/gold/purple on its own):
 *
 *   SAFE                -> red     (`biteSafe`)
 *   RUSH (all stacks)   -> yellow  (`biteRush`) - never red/red-orange
 *   CHAOS anticipation, -> purple  (`biteChaos`) - never red
 *   SHRINK (incl. the
 *   minimum-length
 *   fallback), SCORE HIT
 *   JACKPOT             -> gold    (`star`)
 *   WORMHOLE            -> purple  (`biteChaos`)
 *   SPEED TRIAL         -> its own dedicated blue (`speedTrialBlue`) - never RUSH's gold
 *   STARFRUIT           -> gold/white (`starfruitGlow`)
 *
 * SHRINK's negative score NUMBER keeps the existing separate red convention
 * for a loss (`IMPACT_LABEL_COLOR.loss` in `VisualFeedbackController`) -
 * that label lane is intentionally not this palette, since a numeric loss
 * reading red is a different, pre-existing convention from "this event's
 * physical effect is purple". Every spatial primitive (pop/ring/pulse/
 * ghosts/fallback pulse/body reaction) must use this function's result.
 */
export function impactColorForEvent(event: BiteFeedbackEvent): number {
  switch (event.kind) {
    case "safe":
      return GameConfig.colors.biteSafe;
    case "rush":
      return GameConfig.colors.biteRush;
    case "starfruit":
      return GameConfig.colors.starfruitGlow;
    case "speed-trial-success":
      return GameConfig.colors.speedTrialBlue;
    case "chaos":
      switch (event.outcome) {
        case "jackpot":
          return GameConfig.colors.star;
        case "wormhole":
          return GameConfig.colors.biteChaos;
        case "speed-trial":
          return GameConfig.colors.speedTrialBlue;
        case "shrink":
        case "score-hit":
          return GameConfig.colors.biteChaos;
      }
  }
}

/** SHRINK's own physical-effect color (ghosts, contracting pulse, minimum-length fallback pulse, head/body reaction) - always CHAOS purple, never `impactColorForEvent`'s per-outcome branching, so a caller can never accidentally reach for `danger` red here. */
export const SHRINK_EFFECT_COLOR = GameConfig.colors.biteChaos;

/** WORMHOLE's own dedicated purple, used by both its activation pulse and any future WORMHOLE-only spatial primitive - kept distinct from `wormholeCyan`, which stays reserved for the persistent countdown/edge-overlay treatment. */
export const WORMHOLE_EFFECT_COLOR = GameConfig.colors.biteChaos;

/** SPEED TRIAL's own dedicated color for its badge/edge-streak - never RUSH's gold. */
export const SPEED_TRIAL_COLOR = GameConfig.colors.speedTrialBlue;

/**
 * PR31 follow-up (score feedback). A SEPARATE three-way accent, deliberately
 * NOT `impactColorForEvent()` above - that locked table answers "what color
 * is this event's physical effect AT THE BITE CELL" (SAFE reads red there,
 * matching its apple shape), which is the wrong question for "what color
 * should the score NUMBER'S brief positive bump read as" (red there would
 * read as a loss, the opposite of the intended meaning). Every ordinary
 * score increase (SAFE, a completed SPEED TRIAL, any other bite that simply
 * adds score) reads as the game's own core green; RUSH's own gold/yellow
 * and JACKPOT's own gold are called out only because the spec explicitly
 * wants them distinguishable from an ordinary gain, not because this
 * reintroduces `impactColorForEvent`'s full per-outcome branching.
 */
export type ScoreFeedbackAccent = "positive" | "rush" | "jackpot";

export const SCORE_FEEDBACK_ACCENT_COLOR: Record<ScoreFeedbackAccent, number> = {
  positive: GameConfig.colors.snakeHead,
  rush: GameConfig.colors.biteRush,
  jackpot: GameConfig.colors.star,
};

/** Which `ScoreFeedbackAccent` a score-increasing event's HUD bump should use - RUSH and JACKPOT are called out explicitly, everything else (including a completed SPEED TRIAL) is an ordinary positive gain. */
export function scoreFeedbackAccent(event: BiteFeedbackEvent): ScoreFeedbackAccent {
  if (event.kind === "rush") return "rush";
  if (event.kind === "chaos" && event.outcome === "jackpot") return "jackpot";
  return "positive";
}
